"use client";

import { AnimatePresence, motion } from "framer-motion";
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { IoLogoLinkedin } from "react-icons/io";
import { useOutsideClick } from "../../../hooks/use-outside-click";

const UITeam = ({ cards }) => {
  const [active, setActive] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActive(null);
      }
    };

    document.body.style.overflow = active ? "hidden" : "auto";
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active]);

  useOutsideClick(ref, () => setActive(null));

  const handleImageLoad = useCallback((id) => {
    setImageLoading(prev => ({ ...prev, [id]: false }));
  }, []);

  const handleCardClick = useCallback((card) => {
    setActive(card);
    setImageLoading(prev => ({ ...prev, [card.id]: true }));
  }, []);

  const renderModal = () => {
    if (!active) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 md:p-6"
      >
        <motion.div
          ref={ref}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto relative"
        >
          <motion.button
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 90 }}
            transition={{ duration: 0.2 }}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-lg transition-all duration-200 hover:bg-white"
            onClick={() => setActive(null)}
            aria-label="Close modal"
          >
            <CloseIcon />
          </motion.button>

          <div className="relative">
            <div className="relative h-48 sm:h-64 md:h-80 lg:h-96">
              {imageLoading[active.id] && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              )}
              <div className="w-full h-full">
                <img
                  src={active.photo}
                  alt={active.name}
                  className="w-full h-full object-contain rounded-t-2xl transition-opacity duration-300"
                  onLoad={() => handleImageLoad(active.id)}
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <div 
                  className="flex gap-2 group cursor-pointer transition-all duration-300 rounded-lg"
                  onClick={() => active.linkedin && window.open(active.linkedin, '_blank', 'noopener,noreferrer')}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && active.linkedin && window.open(active.linkedin, '_blank', 'noopener,noreferrer')}
                >
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-white drop-shadow-lg group-hover:text-blue-300 transition-colors duration-200">
                    {active.name}
                  </h3>
                  {active.linkedin && (
                    <a
                      href={active.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white group-hover:text-blue-300 text-xl md:text-3xl transition-colors duration-200"
                      aria-label={`${active.name} LinkedIn`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IoLogoLinkedin />
                    </a>
                  )}
                </div>
                <div className="text-base sm:text-lg font-semibold text-blue-500 drop-shadow-lg group-hover:text-blue-300 transition-colors duration-200">
                  {active.designation}
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 md:p-8">
              <div className="mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">About</h3>
                <div className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {typeof active.content === "function" ? (
                    active.content()
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{ __html: active.description }}
                      className="text-base font-normal text-gray-600"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="py-8 w-full flex flex-col">
      <AnimatePresence mode="wait">
        {active && renderModal()}
      </AnimatePresence>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6 px-4">
        {cards.map((card) => (
          <div
            key={card.id}
            onClick={() => handleCardClick(card)}
            className="bg-slate-100 hover:bg-slate-200 rounded-lg shadow p-4 flex flex-col items-center text-center cursor-pointer transition-all duration-300 hover:scale-105"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && handleCardClick(card)}
          >
            <div className="h-40 w-40 sm:h-48 sm:w-48 overflow-hidden rounded-full mb-4 relative">
              {imageLoading[card.id] && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse" />
              )}
              <img
                src={card.photo}
                alt={card.name}
                className="w-full h-full object-cover object-top rounded-full"
                onLoad={() => handleImageLoad(card.id)}
                loading="lazy"
              />
            </div>

            <h3 className="font-semibold text-lg text-gradient bg-gradient-to-tr from-base_900 to-base_500 bg-clip-text text-transparent">
              {card.name}
            </h3>

            <p className="text-sm text-gray-700 mt-1">
              {card.designation}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

UITeam.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      designation: PropTypes.string.isRequired,
      description: PropTypes.string,
      linkedin: PropTypes.string,
      content: PropTypes.func
    })
  ).isRequired
};

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5 text-black"
  >
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

export default memo(UITeam);